import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api/homeapi";
import { StaffFrame } from "./DashboardShared";
import { useAuth } from "../context/AuthContext";
import { InboxIcon, MessageIcon, PinIcon, PlusIcon, SendIcon, PhoneIcon, WhatsAppIcon, TrashIcon, UsersIcon } from "../components/Icons";

const C = { green:"#006B3C", greenDk:"#004D2B", greenLt:"#E8F5EE", gold:"#B8933A", grayBd:"#E0E0E0", gray:"#F5F5F5", text:"#1A1A1A", textSub:"#666" };

// Actualisation automatique de la messagerie - pas besoin de recharger la
// page pour voir les nouveaux messages.
const POLL_MS = 5000;

const TABS = [
  { id: "INBOX",    label: "Boîte de réception",    icon: InboxIcon,   desc: "Contact & réclamations" },
  { id: "INTERNAL", label: "Communication interne", icon: MessageIcon, desc: "Entre membres du personnel" },
  { id: "WALL",     label: "Mur d'annonces",        icon: PinIcon,     desc: "Annonces pour le personnel" },
];

export default function AdminMessages() {
  const { user } = useAuth();
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes("ROLE_ADMIN");
  const isSupport = roles.includes("ROLE_SUPPORT");
  const canSeeInbox = isAdmin || isSupport;

  const visibleTabs = TABS.filter(t => t.id !== "INBOX" || canSeeInbox);
  const [tab, setTab] = useState(canSeeInbox ? "INBOX" : "INTERNAL");

  const changeTab = (id) => {
    setTab(id);
    if (id === "WALL") {
      // Éteint le badge de notification du menu latéral - l'annonce a été vue.
      localStorage.setItem("wall_last_seen_at", new Date().toISOString());
    }
  };

  return (
    <StaffFrame>
      <div style={{ padding:28, maxWidth:1000, margin:"0 auto" }}>
        <h1 style={{ margin:"0 0 4px", fontSize:22, fontWeight:800 }}>Messagerie</h1>
        <p style={{ margin:"0 0 20px", color:C.textSub, fontSize:13 }}>
          Réclamations des voyageurs, communication interne et annonces pour le personnel.
        </p>

        <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
          {visibleTabs.map(t => {
            const Ic = t.icon;
            return (
              <button key={t.id} onClick={() => changeTab(t.id)}
                style={{
                  display:"flex", alignItems:"center", gap:7,
                  padding:"9px 16px", borderRadius:10, border:`1.5px solid ${tab===t.id?C.green:C.grayBd}`,
                  background: tab===t.id ? C.greenLt : "#fff", color: tab===t.id ? C.greenDk : C.textSub,
                  fontWeight:700, fontSize:13, cursor:"pointer",
                }}>
                <Ic size={15}/> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "INBOX" && canSeeInbox && <ConversationPanel category={null} emptyText="Aucun message de contact ou réclamation pour le moment." />}
        {tab === "INTERNAL" && <ConversationPanel category="INTERNAL" placeholder="Écrire au personnel…" emptyText="Aucune conversation interne pour le moment." allowCreate />}
        {tab === "WALL" && <AnnouncementWall canPost={isAdmin} />}
      </div>
    </StaffFrame>
  );
}

/* ═══════════════════════════════════════
   Boîte de réception / communication interne (liste + fil)
   Actualisation automatique en tâche de fond - pas de rechargement manuel.
═══════════════════════════════════════ */
function ConversationPanel({ category, placeholder, emptyText, allowCreate }) {
  const [list, setList]   = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply]   = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newBody, setNewBody] = useState("");
  const [recipient, setRecipient] = useState(null); // membre du personnel ciblé (annuaire)
  const [directory, setDirectory] = useState([]);
  const [showDirectory, setShowDirectory] = useState(false);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (!allowCreate) return; // l'annuaire n'a de sens que pour la messagerie interne
    api.get("/api/admin/staff-directory").then(r => setDirectory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [allowCreate]);

  const startWith = (person) => {
    setRecipient(person);
    setNewSubject(person.name);
    setShowDirectory(false);
    setShowNew(true);
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = category ? { category } : {};
      const r = await api.get("/api/admin/conversations", { params });
      setList(Array.isArray(r.data) ? r.data : []);
    } catch { /* silencieux - on retentera au prochain cycle */ }
    finally { if (!silent) setLoading(false); }
  }, [category]);

  const openThread = useCallback(async (id, silent = false) => {
    if (!silent) setActive(id);
    try {
      const r = await api.get(`/api/admin/conversations/${id}`);
      setThread(r.data);
    } catch { if (!silent) setThread(null); }
  }, []);

  // Chargement initial + actualisation temps réel (polling léger)
  useEffect(() => {
    load();
    const interval = setInterval(() => {
      load(true);
      if (activeRef.current) openThread(activeRef.current, true);
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [load, openThread]);

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    try {
      await api.post(`/api/admin/conversations/${active}/reply`, { body: reply.trim() });
      setReply("");
      openThread(active);
      load(true);
    } catch {}
  };

  const createInternal = async () => {
    if (!newBody.trim()) return;
    try {
      const r = await api.post("/api/admin/conversations/internal", {
        subject: newSubject.trim() || "Communication interne",
        body: newBody.trim(),
        recipientUserId: recipient?.id || null,
      });
      setShowNew(false); setNewSubject(""); setNewBody(""); setRecipient(null);
      load();
      openThread(r.data.id);
    } catch {}
  };

  const activeConv = list.find(c => c.id === active);
  const isContactLead = activeConv?.category === "CONTACT"; // visiteur anonyme, ne peut pas lire une réponse in-app

  return (
    <div style={{ display:"grid", gridTemplateColumns:"280px 1fr", gap:16, minHeight:420 }}>
      {/* Liste des fils */}
      <div style={{ border:`1px solid ${C.grayBd}`, borderRadius:12, overflow:"hidden", background:"#fff" }}>
        {allowCreate && (
          <div style={{ display:"flex", borderBottom:`1px solid ${C.grayBd}` }}>
            <button onClick={() => { setShowDirectory(v => !v); setShowNew(false); }}
              style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"center", flex:1, padding:"10px", background: showDirectory ? C.greenLt : "#fff", border:"none", borderRight:`1px solid ${C.grayBd}`, fontWeight:700, fontSize:12, color:C.greenDk, cursor:"pointer" }}>
              <UsersIcon size={14}/> Personnel
            </button>
            <button onClick={() => { setShowNew(v => !v); setShowDirectory(false); setRecipient(null); }}
              style={{ display:"flex", alignItems:"center", gap:6, justifyContent:"center", flex:1, padding:"10px", background: showNew ? C.greenLt : "#fff", border:"none", fontWeight:700, fontSize:12, color:C.greenDk, cursor:"pointer" }}>
              <PlusIcon size={14}/> Nouveau
            </button>
          </div>
        )}
        {showDirectory && (
          <div style={{ maxHeight:220, overflowY:"auto", borderBottom:`1px solid ${C.grayBd}` }}>
            {directory.length === 0 && <div style={{ padding:14, fontSize:12, color:C.textSub }}>Aucun autre membre du personnel pour le moment.</div>}
            {directory.map(p => (
              <div key={p.id} onClick={() => startWith(p)}
                style={{ padding:"10px 14px", borderBottom:`1px solid ${C.grayBd}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontWeight:600, fontSize:13 }}>{p.name}</div>
                  <div style={{ fontSize:11, color:C.textSub }}>{p.roleLabel}</div>
                </div>
                <SendIcon size={13} color={C.textSub}/>
              </div>
            ))}
          </div>
        )}
        {showNew && (
          <div style={{ padding:12, borderBottom:`1px solid ${C.grayBd}`, background:C.gray }}>
            {recipient && (
              <div style={{ fontSize:12, color:C.greenDk, marginBottom:6, fontWeight:600 }}>À : {recipient.name} ({recipient.roleLabel})</div>
            )}
            <input placeholder="Sujet" value={newSubject} onChange={e=>setNewSubject(e.target.value)}
              style={{ width:"100%", marginBottom:6, padding:"6px 8px", borderRadius:6, border:`1px solid ${C.grayBd}`, fontSize:12 }} />
            <textarea placeholder="Message…" value={newBody} onChange={e=>setNewBody(e.target.value)} rows={3}
              style={{ width:"100%", marginBottom:6, padding:"6px 8px", borderRadius:6, border:`1px solid ${C.grayBd}`, fontSize:12, resize:"vertical" }} />
            <button onClick={createInternal} style={{ width:"100%", background:C.green, color:"#fff", border:"none", borderRadius:6, padding:"6px", fontWeight:700, fontSize:12, cursor:"pointer" }}>Envoyer</button>
          </div>
        )}
        {loading && <div style={{ padding:16, fontSize:13, color:C.textSub }}>Chargement…</div>}
        {!loading && list.length === 0 && <div style={{ padding:16, fontSize:13, color:C.textSub }}>{emptyText}</div>}
        {list.map(c => (
          <div key={c.id} onClick={() => openThread(c.id)}
            style={{
              padding:"12px 14px", borderBottom:`1px solid ${C.grayBd}`, cursor:"pointer",
              background: active===c.id ? C.greenLt : "#fff",
            }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontWeight:700, fontSize:13 }}>{c.guestName || c.createdBy || "Personnel"}</span>
              {c.status === "OPEN" && <span style={{ width:8, height:8, borderRadius:"50%", background:C.gold }} />}
            </div>
            <div style={{ fontSize:12, color:C.textSub, marginTop:2 }}>{c.subject || "(sans sujet)"}</div>
            {c.lastMessage && <div style={{ fontSize:11, color:"#999", marginTop:4, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{c.lastMessage.body}</div>}
          </div>
        ))}
      </div>

      {/* Fil de discussion */}
      <div style={{ border:`1px solid ${C.grayBd}`, borderRadius:12, background:"#fff", display:"flex", flexDirection:"column" }}>
        {!thread ? (
          <div style={{ margin:"auto", color:C.textSub, fontSize:13 }}>Sélectionnez une conversation à gauche.</div>
        ) : (
          <>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.grayBd}` }}>
              <div style={{ fontWeight:700 }}>{thread.conversation.subject}</div>
              <div style={{ fontSize:12, color:C.textSub }}>
                {thread.conversation.guestName ? `${thread.conversation.guestName} (${thread.conversation.guestEmail})` : thread.conversation.createdBy}
              </div>
            </div>

            {/* Message de contact anonyme : la personne ne peut pas lire une
                réponse dans l'app - on met en avant ses coordonnées pour la
                recontacter directement (appel ou WhatsApp) plutôt qu'un
                champ de réponse inutilisable. */}
            {isContactLead && (
              <div style={{ padding:"12px 16px", background:"#FFFBEF", borderBottom:`1px solid ${C.grayBd}` }}>
                <p style={{ fontSize:12, color:"#92400E", margin:"0 0 10px" }}>
                  Ce message vient du formulaire de contact public - la personne n'est pas connectée et ne pourra pas lire de réponse ici. Recontactez-la directement :
                </p>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {thread.conversation.guestPhone ? (
                    <>
                      <a href={`tel:${thread.conversation.guestPhone}`} style={{ display:"flex", alignItems:"center", gap:6, background:C.green, color:"#fff", borderRadius:8, padding:"8px 14px", fontSize:13, fontWeight:700, textDecoration:"none" }}>
                        <PhoneIcon size={14}/> Appeler {thread.conversation.guestPhone}
                      </a>
                      <a href={`https://wa.me/${thread.conversation.guestPhone.replace(/\D/g,"")}`} target="_blank" rel="noreferrer"
                        style={{ display:"flex", alignItems:"center", gap:6, background:"#25D366", color:"#fff", borderRadius:8, padding:"8px 14px", fontSize:13, fontWeight:700, textDecoration:"none" }}>
                        <WhatsAppIcon size={14}/> WhatsApp
                      </a>
                    </>
                  ) : (
                    <span style={{ fontSize:12, color:"#92400E" }}>Aucun numéro fourni - répondez par e-mail à {thread.conversation.guestEmail}.</span>
                  )}
                </div>
              </div>
            )}

            <div style={{ flex:1, overflowY:"auto", padding:16, display:"flex", flexDirection:"column", gap:10, maxHeight:340 }}>
              {thread.messages.map(m => (
                <div key={m.id} style={{ alignSelf: m.authorRole && m.authorRole !== "VISITEUR" ? "flex-end" : "flex-start", maxWidth:"75%" }}>
                  <div style={{
                    background: m.authorRole && m.authorRole !== "VISITEUR" ? C.green : C.gray,
                    color: m.authorRole && m.authorRole !== "VISITEUR" ? "#fff" : C.text,
                    borderRadius:12, padding:"8px 12px", fontSize:13,
                  }}>
                    {m.body}
                  </div>
                  <div style={{ fontSize:10, color:C.textSub, marginTop:3, textAlign: m.authorRole && m.authorRole !== "VISITEUR" ? "right" : "left" }}>
                    {m.authorName} · {new Date(m.createdAt).toLocaleString("fr-FR")}
                  </div>
                </div>
              ))}
            </div>

            {!isContactLead && (
              <div style={{ display:"flex", gap:8, padding:12, borderTop:`1px solid ${C.grayBd}` }}>
                <input value={reply} onChange={e=>setReply(e.target.value)} placeholder={placeholder || "Répondre…"}
                  onKeyDown={e => e.key === "Enter" && sendReply()}
                  style={{ flex:1, border:`1px solid ${C.grayBd}`, borderRadius:8, padding:"8px 12px", fontSize:13 }} />
                <button onClick={sendReply} style={{ display:"flex", alignItems:"center", gap:6, background:C.green, color:"#fff", border:"none", borderRadius:8, padding:"0 16px", fontWeight:700, cursor:"pointer" }}>
                  <SendIcon size={14}/> Envoyer
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   Mur d'annonces internes (personnel) - actualisation automatique
═══════════════════════════════════════ */
function AnnouncementWall({ canPost }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody]   = useState("");
  const [pinned, setPinned] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get("/api/admin/announcements"); setItems(Array.isArray(r.data) ? r.data : []); }
    catch {} finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const post = async () => {
    if (!title.trim() || !body.trim()) return;
    try {
      await api.post("/api/admin/announcements", { title: title.trim(), body: body.trim(), pinned });
      setTitle(""); setBody(""); setPinned(false);
      load();
    } catch {}
  };

  const remove = async (id) => {
    try { await api.delete(`/api/admin/announcements/${id}`); load(); } catch {}
  };

  const REACTION_EMOJIS = ["👍", "❤️", "😀", "🎉"];

  const react = async (id, emoji) => {
    // Mise à jour optimiste pour une sensation instantanée, corrigée au
    // prochain cycle d'actualisation si le serveur renvoie autre chose.
    setItems(prev => prev.map(a => {
      if (a.id !== id) return a;
      const mine = a.myReactions || [];
      const reactions = { ...(a.reactions || {}) };
      const already = mine.includes(emoji);
      reactions[emoji] = Math.max(0, (reactions[emoji] || 0) + (already ? -1 : 1));
      return { ...a, reactions, myReactions: already ? mine.filter(e => e !== emoji) : [...mine, emoji] };
    }));
    try { await api.post(`/api/admin/announcements/${id}/react`, { emoji }); load(true); } catch { load(true); }
  };

  return (
    <div>
      {canPost && (
        <div style={{ border:`1px solid ${C.grayBd}`, borderRadius:12, padding:16, marginBottom:20, background:"#fff" }}>
          <input placeholder="Titre de l'annonce" value={title} onChange={e=>setTitle(e.target.value)}
            style={{ width:"100%", marginBottom:8, padding:"8px 10px", borderRadius:8, border:`1px solid ${C.grayBd}`, fontSize:13 }} />
          <textarea placeholder="Contenu…" value={body} onChange={e=>setBody(e.target.value)} rows={3}
            style={{ width:"100%", marginBottom:8, padding:"8px 10px", borderRadius:8, border:`1px solid ${C.grayBd}`, fontSize:13, resize:"vertical" }} />
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:C.textSub, marginBottom:10 }}>
            <input type="checkbox" checked={pinned} onChange={e=>setPinned(e.target.checked)} /> Épingler en haut du mur
          </label>
          <button onClick={post} style={{ display:"flex", alignItems:"center", gap:6, background:C.green, color:"#fff", border:"none", borderRadius:8, padding:"8px 18px", fontWeight:700, fontSize:13, cursor:"pointer" }}>
            <PinIcon size={14}/> Publier l'annonce
          </button>
        </div>
      )}

      {loading && <p style={{ color:C.textSub, fontSize:13 }}>Chargement…</p>}
      {!loading && items.length === 0 && <p style={{ color:C.textSub, fontSize:13 }}>Aucune annonce pour le moment.</p>}
      {items.map(a => (
        <div key={a.id} style={{
          border:`1px solid ${a.pinned ? C.gold : C.grayBd}`, borderRadius:12, padding:16, marginBottom:12,
          background: a.pinned ? "#FFFBEF" : "#fff",
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontWeight:700, fontSize:14 }}>
              {a.pinned && <PinIcon size={14}/>}{a.title}
            </div>
            {canPost && (
              <button onClick={() => remove(a.id)} style={{ display:"flex", alignItems:"center", gap:4, background:"none", border:"none", color:"#B91C1C", cursor:"pointer", fontSize:12 }}>
                <TrashIcon size={13}/> Supprimer
              </button>
            )}
          </div>
          <p style={{ fontSize:13, color:C.text, margin:"8px 0", whiteSpace:"pre-wrap" }}>{a.body}</p>
          <div style={{ fontSize:11, color:C.textSub, marginBottom:10 }}>{a.authorName} · {new Date(a.createdAt).toLocaleString("fr-FR")}</div>

          <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
            {REACTION_EMOJIS.map(emoji => {
              const count = a.reactions?.[emoji] || 0;
              const mine = a.myReactions?.includes(emoji);
              return (
                <button key={emoji} onClick={() => react(a.id, emoji)}
                  style={{
                    display:"flex", alignItems:"center", gap:4, fontSize:13, padding:"4px 9px", borderRadius:20,
                    border:`1.5px solid ${mine ? C.green : C.grayBd}`, background: mine ? C.greenLt : "#fff",
                    color: mine ? C.greenDk : C.textSub, cursor:"pointer", fontWeight: mine ? 700 : 500,
                  }}>
                  <span>{emoji}</span>{count > 0 && <span>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
